import React, { useState } from 'react'
import {
  Box,
  Typography,
  Checkbox,
  Button,
  Stack,
  Tooltip,
  Divider,
} from '@mui/material'
import { useAppTheme } from '@/theme/ThemeContext'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import GridOnIcon from '@mui/icons-material/GridOn'
import CheckmarkIcon from '@/assets/checkmark-circle-01.svg'

interface Step4DataVerificationProps {
  mappings: Record<string, string>
  selectedKeyColumns: string[]
  onBack: () => void
  onConfirm: () => void
  fileName?: string
}

const SYSTEM_LABELS: Record<string, string> = {
  water_meter: 'מספר כרטיס מים',
  internal_meter: 'מספר מד פנימי',
  reading: 'קריאה',
  address: 'כתובת נכס',
  phone: 'טלפון',
  payer_id: 'מספר משלם',
  flow: 'ספיקה',
  coordinates: 'קואורדינטות',
  email: 'דוא"ל לקוח',
  longitude: 'longitude',
  latitude: 'latitude',
  usage_type: 'סוג שימוש',
  meter_type: 'סוג מד',
  diameter: 'קוטר'
}

const MOCK_MAPPINGS: Record<string, string> = {
  water_meter: 'מספר כרטיס מים',
  internal_meter: 'מספר מד פנימי',
  phone: 'טלפון פריוריטי',
  address: 'כתובת נכס',
  meter_type: 'סוג מד',
  diameter: 'קוטר',
  usage_type: 'מהות סוג שימוש',
  longitude: 'longitude',
  latitude: 'latitude',
  reading: 'הערה נוכחית'
}

export const Step4DataVerification: React.FC<Step4DataVerificationProps> = ({
  mappings,
  selectedKeyColumns: _selectedKeyColumns,
  onBack,
  onConfirm,
  fileName = 'updatefile010122.xslx'
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const [confirmed, setConfirmed] = useState(false)

  const TEXT_PRIMARY = isDarkMode ? '#F8FAFC' : '#003F61'
  const TEXT_SECONDARY = isDarkMode ? '#94A3B8' : '#64748B'
  const BORDER_COLOR = isDarkMode ? '#2A364F' : '#E2E8F0'

  const activeMappings = Object.keys(mappings).length > 0 ? mappings : MOCK_MAPPINGS
  const mappedEntries = Object.entries(activeMappings).filter(([_id, val]) => val)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, width: '100%', direction: 'rtl', height: '100%' }}>
      {/* Top Instruction Text */}
      <Typography 
        sx={{ 
          color: '#003F61', 
          fontWeight: 700, 
          fontSize: '15px', 
          textAlign: 'right',
          fontFamily: 'Rubik',
          mb: 1
        }}
      >
        הנתונים מוכנים לעדכון במערכת. <Typography component="span" sx={{ fontWeight: 800 }}>לאחר האישור, השינויים יוחלו ולא ניתן יהיה לבטל את הפעולה.</Typography>
      </Typography>

      <Box sx={{ display: 'flex', gap: 4, flex: 1, minHeight: 0 }}>
        
        {/* Right Section: Mappings List */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden'
        }}>
          {/* Header */}
          <Box sx={{ display: 'flex', borderBottom: `1.5px solid ${BORDER_COLOR}`, pb: 1.5, mb: 1 }}>
            <Typography sx={{ flex: 1, textAlign: 'right', color: '#72A4EB', fontWeight: 600, fontSize: '14px', fontFamily: 'Rubik' }}>
              כותרת במערכת
            </Typography>
            <Typography sx={{ flex: 1, textAlign: 'right', color: '#72A4EB', fontWeight: 600, fontSize: '14px', fontFamily: 'Rubik' }}>
              כותרת בקובץ
            </Typography>
          </Box>

          <Box sx={{ 
            flex: 1, 
            overflowY: 'auto', 
            pr: 1,
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#E2E8F0', borderRadius: '4px' }
          }}>
            {mappedEntries.map(([sysId, fileCol]) => (
              <Box 
                key={sysId} 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  py: 1.5
                }}
              >
                {/* System Column (Right) */}
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Tooltip title="ערך לדוגמא מהקובץ" arrow placement="top">
                    <InfoOutlinedIcon sx={{ color: '#94A3B8', fontSize: 18, cursor: 'pointer' }} />
                  </Tooltip>
                  <Typography sx={{ color: TEXT_PRIMARY, fontSize: '14px', fontWeight: 500, fontFamily: 'Rubik' }}>
                    {SYSTEM_LABELS[sysId] || sysId}
                  </Typography>
                </Box>

                {/* File Column (Left) */}
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ color: TEXT_SECONDARY, fontSize: '14px', fontFamily: 'Rubik' }}>
                    {fileCol}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Vertical Divider */}
        <Divider orientation="vertical" flexItem sx={{ borderColor: BORDER_COLOR, opacity: 0.6 }} />

        {/* Left Section: Summary Card */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', pt: 2 }}>
          <Box sx={{ 
            width: '350px',
            height: 'fit-content',
            bgcolor: isDarkMode ? '#1E293B' : '#FFFFFF',
            borderRadius: '16px',
            border: `1px solid ${BORDER_COLOR}`,
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.04)',
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 3
          }}>
            {/* Summary Title with Icon (Aligned Right) */}
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ justifyContent: 'flex-start' }}>
               <img src={CheckmarkIcon} alt="success" style={{ width: 22, height: 22 }} />
               <Typography sx={{ fontWeight: 450, fontSize: '16px', color: '#64748B', fontFamily: 'Rubik' }}>
                 סיכום העדכון
               </Typography>
            </Stack>

            {/* File Name Box */}
            <Box sx={{ width: '100%' }}>
              <Typography sx={{ color: '#72A4EB', fontSize: '13px', fontWeight: 600, mb: 1, textAlign: 'right' }}>קובץ העדכון</Typography>
              <Box sx={{ 
                border: '1px solid #E2E8F0', 
                borderRadius: '8px', 
                p: '12px 16px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                bgcolor: isDarkMode ? '#091327' : '#F8FAFC'
              }}>
                <GridOnIcon sx={{ color: '#003F61', fontSize: 20 }} />
                <Typography sx={{ color: '#003F61', fontSize: '14px', fontWeight: 600, fontFamily: 'Rubik' }}>
                  {fileName}
                </Typography>
              </Box>
            </Box>

            {/* Stats */}
            <Stack direction="row" spacing={0} sx={{ width: '100%', justifyContent: 'space-between', px: 2 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ color: '#72A4EB', fontSize: '12px', fontWeight: 600, mb: 1, lineHeight: 1.2 }}>כמות שורות<br/>שיתעדכנו</Typography>
                <Typography sx={{ color: '#003F61', fontSize: '24px', fontWeight: 350 }}>452</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ color: '#72A4EB', fontSize: '12px', fontWeight: 600, mb: 1, lineHeight: 1.2 }}>כמות עמודות<br/>שיתעדכנו</Typography>
                <Typography sx={{ color: '#003F61', fontSize: '24px', fontWeight: 350 }}>21</Typography>
              </Box>
            </Stack>

            {/* Confirmation Checkbox */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, justifyContent: 'flex-start' }}>
              <Checkbox 
                checked={confirmed} 
                onChange={(e) => setConfirmed(e.target.checked)}
                sx={{ 
                  p: 0, 
                  color: '#94A3B8',
                  '&.Mui-checked': { color: '#3B82F6' },
                  '& .MuiSvgIcon-root': { fontSize: 22 }
                }}
              />
              <Typography sx={{ color: '#64748B', fontSize: '13px', fontFamily: 'Rubik', fontWeight: 400 }}>
                אני מאשר/ת את ביצוע העדכון
              </Typography>
            </Box>

            {/* Action Buttons Row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 1, gap: 2 }}>
              <Button
                variant="text"
                onClick={onBack}
                sx={{
                  flex: 1,
                  bgcolor: '#EFF2F7',
                  color: '#64748B',
                  borderRadius: '8px',
                  py: 1.2,
                  fontSize: '14px',
                  fontWeight: 600,
                  fontFamily: 'Rubik',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#E2E8F0' }
                }}
              >
                חזרה לעריכה
              </Button>
              <Button
                variant="contained"
                disabled={!confirmed}
                onClick={onConfirm}
                sx={{
                  flex: 1.5,
                  bgcolor: confirmed ? '#72A4EB' : '#BFD7FF',
                  color: '#FFFFFF',
                  borderRadius: '8px',
                  py: 1.2,
                  fontSize: '14px',
                  fontWeight: 600,
                  fontFamily: 'Rubik',
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: confirmed ? '#5B92D1' : '#BFD7FF', boxShadow: 'none' },
                  '&.Mui-disabled': { bgcolor: '#BFD7FF', color: '#FFFFFF', opacity: 0.6 }
                }}
              >
                אישור ועדכון נתונים
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ borderColor: BORDER_COLOR, opacity: 0.6, mt: 1 }} />
    </Box>
  )
}
